import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppServices } from "../../../../../../../hook/services";
import { toast } from "react-toastify";
import { IconsArray } from "../../../../../constants/Icons";

const DEFAULT_FORM = {
  text: "",
  tooltip: "",
  icon: "",
  iconFamily: "fas",
  colorPicker: "#4f46e5",
  cssSelector: "",
  linkOrCustomValue: "",
  sendContact: true,
  customFields: true,
  user_permissions: {
    agency_admins: true,
    agency_users: true,
    account_admins: true,
    account_users: true,
  },
};

const PERMISSION_LABELS = {
  agency_admins: "Agency admins",
  agency_users: "Agency users",
  account_admins: "Account admins",
  account_users: "Account users",
};

const AddContactButton = ({
  handleRefresh,
  data,
  editIndex = null,
  selectedButton = null,
  onClose = () => {},
}) => {
  const AppService = useAppServices();
  const { companyId, type } = useParams();
  const handler =
    type === "agency" || type === "location"
      ? AppService.sidenav_customizer_main_level
      : type === "group"
      ? AppService.sidenav_customizer_custom_level
      : AppService.sidenav_customizer_saas_level;

  const isEdit = editIndex !== null && selectedButton;

  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [iconSearch, setIconSearch] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isEdit && selectedButton) {
      setFormData({
        ...DEFAULT_FORM,
        ...selectedButton,
        user_permissions: {
          ...DEFAULT_FORM.user_permissions,
          ...(selectedButton.user_permissions || {}),
        },
      });
    }
  }, [isEdit, selectedButton]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    if (!isEdit) {
      setFormData(DEFAULT_FORM);
      setIconSearch("");
    }
    onClose();
  };

  const handleChange = (e) => {
    const { name, type: t, value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: t === "checkbox" ? checked : value,
    }));
  };

  const handlePermissionsChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      user_permissions: { ...prev.user_permissions, [name]: checked },
    }));
  };

  const filteredIcons = useMemo(() => {
    const term = iconSearch.trim().toLowerCase();
    if (!term) return IconsArray;
    return IconsArray.filter((i) => i.name.toLowerCase().includes(term));
  }, [iconSearch]);

  const setIcon = (name, family) => {
    setFormData((prev) => ({ ...prev, icon: name, iconFamily: family }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.text.trim()) {
      toast.warning("Button text is required.");
      return;
    }
    if (!formData.linkOrCustomValue.trim()) {
      toast.warning("A URL or custom value is required.");
      return;
    }

    const button = {
      ...formData,
      icon: formData.icon || null,
      iconFamily: formData.icon ? formData.iconFamily : null,
    };

    const contact_buttons = data?.contact_buttons
      ? [...data.contact_buttons]
      : [];

    if (isEdit) {
      contact_buttons[editIndex] = button;
    } else {
      contact_buttons.push(button);
    }

    const payload = {
      contact_buttons,
      companyId,
      type,
      ...(data?._id && { _id: data._id }),
    };

    try {
      setSubmitting(true);
      const { response } = await handler[data?._id ? "update" : "create"]({
        payload,
      });

      if (response) {
        handleRefresh();
        toast.success(isEdit ? "Button updated!" : "Button added!");
        setOpen(false);
        if (!isEdit) {
          setFormData(DEFAULT_FORM);
          setIconSearch("");
        }
        onClose();
      } else {
        toast.error("Failed to save button.");
      }
    } catch (error) {
      console.error("Error saving contact button:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {isEdit ? (
        <button
          className="btn d-inline-flex align-items-center justify-content-center gap-1"
          title="Edit"
          onClick={handleOpen}
          style={{
            borderRadius: 8,
            background: "#eef2ff",
            color: "#4338ca",
            border: "1px solid #c7d2fe",
            fontSize: 12,
            fontWeight: 500,
            padding: "6px 10px",
          }}
        >
          <i className="bi bi-pencil-square" />
          Edit
        </button>
      ) : (
        <button
          className="btn btn-primary d-inline-flex align-items-center gap-2"
          onClick={handleOpen}
          style={{ borderRadius: 10, fontWeight: 500 }}
        >
          <i className="bi bi-plus-lg" />
          Add Contact Button
        </button>
      )}

      {open && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(15, 23, 42, 0.55)", zIndex: 2000 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) handleClose();
          }}
        >
          <style>{`
            .cb-modal .modal-dialog {
              max-height: calc(100vh - 3rem);
              margin-top: 1.5rem;
              margin-bottom: 1.5rem;
            }
            .cb-modal .modal-content {
              border-radius: 16px;
              overflow: hidden;
              border: none;
              box-shadow: 0 24px 48px rgba(15, 23, 42, 0.18);
              max-height: calc(100vh - 3rem);
              display: flex;
              flex-direction: column;
            }
            .cb-modal form {
              display: flex;
              flex-direction: column;
              flex: 1 1 auto;
              min-height: 0;
              overflow: hidden;
            }
            .cb-modal .modal-body {
              flex: 1 1 auto;
              overflow-y: auto;
              min-height: 0;
            }
            .cb-modal .modal-body::-webkit-scrollbar { width: 8px; }
            .cb-modal .modal-body::-webkit-scrollbar-track { background: transparent; }
            .cb-modal .modal-body::-webkit-scrollbar-thumb {
              background: rgba(15, 23, 42, 0.18);
              border-radius: 8px;
            }
            .cb-modal .modal-body::-webkit-scrollbar-thumb:hover {
              background: rgba(15, 23, 42, 0.32);
            }
            .cb-modal .cb-header { flex-shrink: 0; }
            .cb-modal .cb-header {
              background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
              color: #fff;
              padding: 18px 22px;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }
            .cb-modal .cb-header h5 {
              margin: 0;
              font-weight: 600;
              font-size: 18px;
              display: flex;
              align-items: center;
              gap: 10px;
            }
            .cb-modal .cb-header small {
              color: rgba(255, 255, 255, 0.8);
              font-size: 12px;
            }
            .cb-modal .cb-close {
              background: rgba(255, 255, 255, 0.15);
              border: none;
              color: #fff;
              width: 32px;
              height: 32px;
              border-radius: 8px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              transition: background 0.15s;
            }
            .cb-modal .cb-close:hover { background: rgba(255, 255, 255, 0.28); }
            .cb-modal .cb-section-title {
              font-size: 12px;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.04em;
              color: #475569;
              margin-bottom: 10px;
              display: flex;
              align-items: center;
              gap: 6px;
            }
            .cb-modal .cb-card {
              background: #fff;
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              padding: 16px;
            }
            .cb-modal .cb-preview {
              background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
              border: 1px dashed #c7d2fe;
              border-radius: 12px;
              padding: 22px;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 110px;
            }
            .cb-modal .cb-preview-btn {
              border-radius: 8px;
              padding: 8px 16px;
              font-weight: 500;
              font-size: 13px;
              color: #fff;
              border: none;
              display: inline-flex;
              align-items: center;
              gap: 8px;
              box-shadow: 0 4px 12px rgba(15, 23, 42, 0.15);
            }
            .cb-modal .form-label {
              font-size: 13px;
              font-weight: 500;
              color: #334155;
              margin-bottom: 6px;
            }
            .cb-modal .form-control,
            .cb-modal .form-select {
              border-radius: 8px;
              border-color: #e2e8f0;
              font-size: 14px;
            }
            .cb-modal .form-control:focus,
            .cb-modal .form-select:focus {
              border-color: #818cf8;
              box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
            }
            .cb-modal .color-picker-wrap {
              display: flex;
              align-items: center;
              gap: 10px;
            }
            .cb-modal .color-picker-wrap input[type="color"] {
              width: 44px;
              height: 38px;
              padding: 2px;
              border-radius: 8px;
              border: 1px solid #e2e8f0;
              cursor: pointer;
              flex-shrink: 0;
            }
            .cb-modal .icon-search {
              position: relative;
              margin-bottom: 10px;
            }
            .cb-modal .icon-search .bi-search {
              position: absolute;
              left: 12px;
              top: 50%;
              transform: translateY(-50%);
              color: #94a3b8;
            }
            .cb-modal .icon-search input { padding-left: 36px; }
            .cb-modal .icon-grid {
              display: grid;
              grid-template-columns: repeat(8, 1fr);
              gap: 6px;
              max-height: 200px;
              overflow-y: auto;
              padding: 6px;
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 10px;
            }
            .cb-modal .icon-cell {
              aspect-ratio: 1;
              border: 1px solid transparent;
              background: #fff;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              transition: all 0.12s;
              color: #475569;
              font-size: 16px;
            }
            .cb-modal .icon-cell:hover {
              border-color: #c7d2fe;
              transform: translateY(-1px);
              color: #4338ca;
            }
            .cb-modal .icon-cell.active {
              background: #eef2ff;
              border-color: #4f46e5;
              color: #4338ca;
              box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.18);
            }
            .cb-modal .icon-info {
              font-size: 12px;
              color: #64748b;
              margin-top: 8px;
              display: flex;
              align-items: center;
              justify-content: space-between;
            }
            .cb-modal .icon-clear {
              border: none;
              background: transparent;
              color: #ef4444;
              font-size: 12px;
              font-weight: 500;
              padding: 0;
            }
            .cb-modal .icon-clear:hover { text-decoration: underline; }
            .cb-modal .toggle-row {
              display: block;
              padding: 14px 16px;
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              background: #fff;
              cursor: pointer;
              user-select: none;
              transition: all 0.15s;
              margin: 0;
              height: 100%;
            }
            .cb-modal .toggle-row:hover { border-color: #c7d2fe; background: #f8fafc; }
            .cb-modal .toggle-row.is-active {
              border-color: #4f46e5;
              background: #eef2ff;
              box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
            }
            .cb-modal .toggle-row .sr-only-checkbox {
              position: absolute;
              width: 1px;
              height: 1px;
              padding: 0;
              margin: -1px;
              overflow: hidden;
              clip: rect(0, 0, 0, 0);
              white-space: nowrap;
              border: 0;
            }
            .cb-modal .toggle-row-text {
              display: block;
            }
            .cb-modal .toggle-row .toggle-label {
              font-size: 14px;
              font-weight: 600;
              color: #1e293b;
              display: block;
            }
            .cb-modal .toggle-row.is-active .toggle-label { color: #4338ca; }
            .cb-modal .toggle-row .toggle-help {
              font-size: 12px;
              color: #64748b;
              margin-top: 4px;
              display: block;
            }
            .cb-modal .perm-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 8px;
            }
            .cb-modal .modal-footer {
              border-top: 1px solid #e2e8f0;
              padding: 14px 22px;
              background: #f8fafc;
              flex-shrink: 0;
            }
            .cb-modal .btn-cancel {
              background: transparent;
              color: #64748b;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              font-weight: 500;
            }
            .cb-modal .btn-cancel:hover { background: #f1f5f9; color: #1e293b; }
            .cb-modal .btn-save {
              background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
              border: none;
              color: #fff;
              border-radius: 8px;
              font-weight: 500;
              padding: 8px 22px;
            }
            .cb-modal .btn-save:hover { opacity: 0.92; color: #fff; }
            .cb-modal .btn-save:disabled { opacity: 0.6; }
          `}</style>

          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg cb-modal">
            <div className="modal-content">
              <div className="cb-header">
                <div>
                  <h5>
                    <i
                      className={`bi ${
                        isEdit ? "bi-pencil-square" : "bi-plus-square"
                      }`}
                    />
                    {isEdit ? "Edit Contact Button" : "Add Contact Button"}
                  </h5>
                  <small>
                    Configure how this button appears on the contact detail
                    page.
                  </small>
                </div>
                <button
                  type="button"
                  className="cb-close"
                  onClick={handleClose}
                  aria-label="Close"
                >
                  <i className="bi bi-x-lg" />
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body p-4">
                  {/* Live preview */}
                  <div className="cb-preview mb-4">
                    <button
                      type="button"
                      className="cb-preview-btn"
                      style={{ background: formData.colorPicker }}
                      title={formData.tooltip}
                      tabIndex={-1}
                    >
                      {formData.icon && (
                        <i
                          className={`${formData.iconFamily} fa-${formData.icon}`}
                          style={{ fontSize: 14 }}
                        />
                      )}
                      <span>{formData.text || "Button preview"}</span>
                    </button>
                  </div>

                  {/* Identity */}
                  <div className="cb-card mb-3">
                    <div className="cb-section-title">
                      <i className="bi bi-tag" /> Identity
                    </div>
                    <div className="row g-3">
                      <div className="col-md-7">
                        <label className="form-label">Button text</label>
                        <input
                          type="text"
                          className="form-control"
                          name="text"
                          value={formData.text}
                          onChange={handleChange}
                          placeholder="e.g. Send WhatsApp"
                          required
                        />
                      </div>
                      <div className="col-md-5">
                        <label className="form-label">Background color</label>
                        <div className="color-picker-wrap">
                          <input
                            type="color"
                            name="colorPicker"
                            value={formData.colorPicker}
                            onChange={handleChange}
                          />
                          <input
                            type="text"
                            className="form-control"
                            name="colorPicker"
                            value={formData.colorPicker}
                            onChange={handleChange}
                            placeholder="#4f46e5"
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <label className="form-label">
                          Tooltip{" "}
                          <span className="text-muted small">(HTML allowed)</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="tooltip"
                          value={formData.tooltip}
                          onChange={handleChange}
                          placeholder="Shown when hovering the button"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Icon */}
                  <div className="cb-card mb-3">
                    <div className="cb-section-title">
                      <i className="bi bi-emoji-smile" /> Icon (optional)
                    </div>
                    <div className="icon-search">
                      <i className="bi bi-search" />
                      <input
                        type="text"
                        className="form-control"
                        value={iconSearch}
                        onChange={(e) => setIconSearch(e.target.value)}
                        placeholder="Search icons..."
                      />
                    </div>

                    <div className="icon-grid">
                      {filteredIcons.length === 0 ? (
                        <div
                          className="text-center text-muted small py-3"
                          style={{ gridColumn: "1 / -1" }}
                        >
                          No icons match "{iconSearch}"
                        </div>
                      ) : (
                        filteredIcons.map((ic) => (
                          <div
                            key={`${ic.fontFamily}-${ic.name}`}
                            className={`icon-cell ${
                              formData.icon === ic.name &&
                              formData.iconFamily === ic.fontFamily
                                ? "active"
                                : ""
                            }`}
                            onClick={() => setIcon(ic.name, ic.fontFamily)}
                            title={ic.name}
                          >
                            <i className={`${ic.fontFamily} fa-${ic.name}`} />
                          </div>
                        ))
                      )}
                    </div>

                    <div className="icon-info">
                      <span>
                        {formData.icon ? (
                          <>
                            Selected: <strong>{formData.icon}</strong>
                          </>
                        ) : (
                          <span className="text-muted">No icon selected</span>
                        )}
                      </span>
                      {formData.icon && (
                        <button
                          type="button"
                          className="icon-clear"
                          onClick={() => setIcon("", "fas")}
                        >
                          <i className="bi bi-x-circle me-1" />
                          Remove icon
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Behaviour */}
                  <div className="cb-card mb-3">
                    <div className="cb-section-title">
                      <i className="bi bi-arrow-up-right-circle" /> Click
                      behaviour
                    </div>
                    <div className="mb-3">
                      <label className="form-label">
                        Link or custom value
                      </label>
                      <input
                        type="text"
                        name="linkOrCustomValue"
                        className="form-control"
                        placeholder="https://example.com or {{custom_value.x}}"
                        value={formData.linkOrCustomValue}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="row g-2">
                      <div className="col-md-6">
                        <label
                          htmlFor="cb-send-contact"
                          className={`toggle-row ${formData.sendContact ? "is-active" : ""}`}
                        >
                          <input
                            id="cb-send-contact"
                            type="checkbox"
                            name="sendContact"
                            checked={!!formData.sendContact}
                            onChange={handleChange}
                            className="sr-only-checkbox"
                          />
                          <span className="toggle-row-text">
                            <span className="toggle-label">
                              Send contact data
                            </span>
                            <span className="toggle-help">
                              Pass first name, email, phone, etc. as URL
                              params.
                            </span>
                          </span>
                        </label>
                      </div>
                      <div className="col-md-6">
                        <label
                          htmlFor="cb-custom-fields"
                          className={`toggle-row ${formData.customFields ? "is-active" : ""}`}
                        >
                          <input
                            id="cb-custom-fields"
                            type="checkbox"
                            name="customFields"
                            checked={!!formData.customFields}
                            onChange={handleChange}
                            className="sr-only-checkbox"
                          />
                          <span className="toggle-row-text">
                            <span className="toggle-label">
                              Send custom fields
                            </span>
                            <span className="toggle-help">
                              Append all contact custom fields to the URL.
                            </span>
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Permissions */}
                  <div className="cb-card mb-3">
                    <div className="cb-section-title">
                      <i className="bi bi-shield-lock" /> Visible to
                    </div>
                    <div className="perm-grid">
                      {Object.keys(formData.user_permissions).map((key) => (
                        <label
                          htmlFor={`cb-perm-${key}`}
                          key={key}
                          className={`toggle-row ${formData.user_permissions[key] ? "is-active" : ""}`}
                        >
                          <input
                            id={`cb-perm-${key}`}
                            type="checkbox"
                            name={key}
                            checked={!!formData.user_permissions[key]}
                            onChange={handlePermissionsChange}
                            className="sr-only-checkbox"
                          />
                          <span className="toggle-row-text">
                            <span className="toggle-label">
                              {PERMISSION_LABELS[key] || key}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Advanced */}
                  <div className="cb-card">
                    <div className="cb-section-title">
                      <i className="bi bi-sliders" /> Advanced
                    </div>
                    <label className="form-label">CSS selector / id</label>
                    <input
                      type="text"
                      name="cssSelector"
                      className="form-control"
                      value={formData.cssSelector}
                      onChange={handleChange}
                      placeholder="my-custom-button"
                    />
                    <p className="text-muted small mb-0 mt-2">
                      Used as the rendered button's id and class for custom
                      styling.
                    </p>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-cancel"
                    onClick={handleClose}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-save"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving...
                      </>
                    ) : isEdit ? (
                      "Update button"
                    ) : (
                      "Create button"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AddContactButton;
